import { MoneyDirection } from "@common/constants";
import { JournalEntrySourceFilter } from "@common/schemas/domains/accounting/journalEntry";
import {
    EliminationDetailOpeningBalance,
    EliminationDetailReportResponse,
} from "@common/schemas/domains/accounting/elimination.schema";
import {
    compareAccountCodes,
    preferCodeText,
} from "@common/helpers/accounting";
import { EliminationDetailViewRow } from "./eliminationDetailViewConfig";

type EliminationDetailReportRow =
    EliminationDetailReportResponse["rows"][number];

interface EliminationDetailFilterInput {
    entityIds?: number[];
    accountCodes?: number[];
    counterpartyIds?: string[];
    journalEntrySources?: JournalEntrySourceFilter[];
    minAmount?: number;
    maxAmount?: number;
    direction?: MoneyDirection;
}

interface EliminationDetailBuildOptions extends EliminationDetailFilterInput {
    openingBalances?: EliminationDetailOpeningBalance[];
}

interface AccountLabelInput {
    accountCode: number | null;
    accountCodeText?: string | null;
    accountName: string;
}

function toAccountLabel({
    accountCode,
    accountCodeText,
    accountName,
}: AccountLabelInput): string {
    const codeText = preferCodeText(accountCodeText, accountCode);
    return codeText !== null ? `${codeText} - ${accountName}` : accountName;
}

/**
 * Elimination rows carry only a coarse `origin` (engine | manual), so the
 * general-ledger journal-entry-source filter is mapped: `ManualJournalEntry`
 * selects manual eliminations and any other (transaction-derived) source
 * selects engine eliminations. It cannot distinguish among engine sub-sources.
 */
export function matchesEliminationSource(
    origin: "engine" | "manual",
    sources: JournalEntrySourceFilter[] | undefined,
): boolean {
    if (!sources || sources.length === 0) {
        return true;
    }
    const wantsManual = sources.includes(
        JournalEntrySourceFilter.ManualJournalEntry,
    );
    const wantsEngine = sources.some(
        (source) => source !== JournalEntrySourceFilter.ManualJournalEntry,
    );
    return origin === "manual" ? wantsManual : wantsEngine;
}

/**
 * Projects the backend elimination-detail rows into the nested general-ledger
 * shape (an expandable account row wrapping a Beginning balance row, the
 * elimination lines, and an Ending balance row), applying the entity filter and
 * the general-ledger-style filters (account / counterparty / source / amount /
 * direction) client-side. Accounts with no surviving lines drop out.
 *
 * The running balance starts from the account's opening balance (balance-sheet
 * eliminations dated before the period) and accumulates the *filtered* lines,
 * so the Balance column and Ending balance tie out to what is displayed, the
 * same way a general-ledger account carries its opening balance.
 */
export function buildEliminationDetailRows(
    rows: EliminationDetailReportRow[],
    options: EliminationDetailBuildOptions,
): EliminationDetailViewRow[] {
    const {
        openingBalances = [],
        entityIds,
        accountCodes,
        counterpartyIds,
        journalEntrySources,
        minAmount,
        maxAmount,
        direction,
    } = options;

    const matchesEntity = (entityId: number) =>
        !entityIds || entityIds.length === 0 || entityIds.includes(entityId);

    const matchesAccountCode = (accountCode: number | null) =>
        !accountCodes ||
        accountCodes.length === 0 ||
        (accountCode !== null && accountCodes.includes(accountCode));

    const accountGroups = new Map<
        string,
        {
            accountLabel: string;
            accountCodeText: string | null;
            openingBalance: number;
            transactions: EliminationDetailViewRow[];
        }
    >();
    for (const openingBalance of openingBalances) {
        if (
            !matchesEntity(openingBalance.entityId) ||
            !matchesAccountCode(openingBalance.accountCode)
        ) {
            continue;
        }
        accountGroups.set(openingBalance.accountId, {
            accountLabel: toAccountLabel(openingBalance),
            accountCodeText: preferCodeText(
                openingBalance.accountCodeText,
                openingBalance.accountCode,
            ),
            openingBalance: openingBalance.amount,
            transactions: [],
        });
    }

    for (const row of rows) {
        const entityId = row.pair.entityId;
        if (!matchesEntity(entityId)) {
            continue;
        }
        if (!matchesAccountCode(row.accountCode)) {
            continue;
        }
        if (
            counterpartyIds &&
            counterpartyIds.length > 0 &&
            !(
                row.counterpartyId !== null &&
                counterpartyIds.includes(row.counterpartyId)
            )
        ) {
            continue;
        }
        if (!matchesEliminationSource(row.origin, journalEntrySources)) {
            continue;
        }
        const absAmount = Math.abs(row.amount);
        if (minAmount !== undefined && absAmount < minAmount) {
            continue;
        }
        if (maxAmount !== undefined && absAmount > maxAmount) {
            continue;
        }
        if (direction === MoneyDirection.MONEY_IN && row.amount < 0) {
            continue;
        }
        if (direction === MoneyDirection.MONEY_OUT && row.amount > 0) {
            continue;
        }

        const group = accountGroups.get(row.accountId) ?? {
            accountLabel: toAccountLabel(row),
            accountCodeText: preferCodeText(
                row.accountCodeText,
                row.accountCode,
            ),
            openingBalance: 0,
            transactions: [],
        };
        group.transactions.push({
            id: row.id,
            rowKind: "transaction",
            entityId,
            entityName: row.entityName,
            origin: row.origin,
            manualJournalEntryId: row.manualJournalEntryId ?? undefined,
            date: row.date,
            counterpartyName: row.counterpartyName,
            journalEntries: row.journalEntries,
            description: row.description,
            split: row.split,
            amount: row.amount,
            balance: 0,
        });
        accountGroups.set(row.accountId, group);
    }

    const sortedGroups = [...accountGroups.entries()].sort(
        ([, first], [, second]) =>
            compareAccountCodes(first.accountCodeText, second.accountCodeText),
    );
    return sortedGroups.map(([accountId, group]) => {
        let runningBalance = group.openingBalance;
        for (const transaction of group.transactions) {
            runningBalance += transaction.amount ?? 0;
            transaction.balance = runningBalance;
        }
        const closingBalance = runningBalance;
        return {
            id: `account:${accountId}`,
            rowKind: "account",
            accountLabel: group.accountLabel,
            closingBalance,
            children: [
                {
                    id: `opening:${accountId}`,
                    rowKind: "openingBalance",
                    balanceValue: group.openingBalance,
                },
                ...group.transactions,
                {
                    id: `closing:${accountId}`,
                    rowKind: "closingBalance",
                    balanceValue: closingBalance,
                },
            ],
        };
    });
}
